/**
 * CrossCollateralSummary
 *
 * Displays a summary card below the deal securities table when one or more
 * securities are linked to multiple facilities (cross-collateralisation).
 *
 * Detection logic (client-side):
 *   Group securities by securityId across all facility entries.
 *   If a security appears in >1 facility → it is cross-collateralised.
 *
 * 6-colour palette for groups (matches spec):
 *   amber-500, blue-500, purple-500, teal-500, rose-500, emerald-500
 *
 * Props:
 *   securities – flat list of DealSecurity records (each includes facilityId)
 *   facilityNameMap – facilityId → display name
 *
 * Rendered:
 *   data-testid="card-cross-collateral-summary"       – outer card
 *   data-testid="allocation-bar"                      – per-group allocation bar
 *
 * Hidden entirely when no cross-collateral exists.
 */

import { Link2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getSecurityDisplayName } from "@/lib/security-display-name";
import { getCanonicalSecurityGroupKey, type CanonicalSecurityEntry } from "@shared/security-canonical";

// 6-colour palette — Tailwind bg classes for group segments
const GROUP_COLOURS = [
  "bg-amber-500",
  "bg-blue-500",
  "bg-purple-500",
  "bg-teal-500",
  "bg-rose-500",
  "bg-emerald-500",
];

// Text colours matching the bg palette (for labels)
const GROUP_TEXT_COLOURS = [
  "text-amber-700",
  "text-blue-700",
  "text-purple-700",
  "text-teal-700",
  "text-rose-700",
  "text-emerald-700",
];

// Dot colours for the legend
const GROUP_DOT_COLOURS = [
  "bg-amber-500",
  "bg-blue-500",
  "bg-purple-500",
  "bg-teal-500",
  "bg-rose-500",
  "bg-emerald-500",
];

// Format currency (AUD)
function formatCurrency(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "-";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

export interface CrossCollateralSecurity extends CanonicalSecurityEntry {
  facilityId: string | null;
  facilityName?: string | null;
  estimatedValue?: string | null;
  securedAmount?: string | null;
}

interface CrossCollateralGroup {
  securityId: string;
  description: string;
  securityType: string;
  estimatedValue: string | null;
  facilities: Array<{
    facilityId: string;
    facilityName: string;
    securedAmount: string | null;
  }>;
  colourIndex: number;
}

interface CrossCollateralSummaryProps {
  securities: CrossCollateralSecurity[];
  facilityNameMap: Record<string, string>;
}

/**
 * Build cross-collateral groups from flat securities list.
 * Returns only canonical securities that appear in 2+ facilities.
 */
function buildCrossCollateralGroups(
  securities: CrossCollateralSecurity[],
  facilityNameMap: Record<string, string>
): CrossCollateralGroup[] {
  // Group by canonical key so legacy duplicate facility-scoped rows collapse
  // into one cross-collateral group, matching the deal register.
  const byCanonicalKey = new Map<string, CrossCollateralSecurity[]>();
  for (const sec of securities) {
    const canonicalKey = getCanonicalSecurityGroupKey(sec);
    const existing = byCanonicalKey.get(canonicalKey);
    if (existing) {
      existing.push(sec);
    } else {
      byCanonicalKey.set(canonicalKey, [sec]);
    }
  }

  const groups: CrossCollateralGroup[] = [];
  let colourIndex = 0;

  for (const entries of Array.from(byCanonicalKey.values())) {
    const linkedEntries = entries.filter(
      (entry): entry is CrossCollateralSecurity & { facilityId: string } => Boolean(entry.facilityId),
    );
    if (linkedEntries.length < 2) continue; // not cross-collateralised

    const first = linkedEntries[0];
    groups.push({
      securityId: first.id,
      description: getSecurityDisplayName(first),
      securityType: first.securityType ?? "security",
      estimatedValue: first.estimatedValue ?? null,
      facilities: linkedEntries.map((entry) => ({
        facilityId: entry.facilityId,
        facilityName:
          entry.facilityName ||
          facilityNameMap[entry.facilityId] ||
          `Facility ${entry.facilityId.slice(0, 6)}`,
        securedAmount: entry.securedAmount ?? null,
      })),
      colourIndex: colourIndex % GROUP_COLOURS.length,
    });
    colourIndex++;
  }

  return groups;
}

export function CrossCollateralSummary({
  securities,
  facilityNameMap,
}: CrossCollateralSummaryProps) {
  const groups = buildCrossCollateralGroups(securities, facilityNameMap);

  // Hidden when no cross-collateral
  if (groups.length === 0) {
    return null;
  }

  return (
    <Card data-testid="card-cross-collateral-summary">
      <CardHeader className="py-3 pb-1">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Link2 className="h-4 w-4 text-amber-500" />
          Cross-Collateralisation
          <span className="text-muted-foreground font-normal">
            ({groups.length} {groups.length === 1 ? "group" : "groups"})
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-0 space-y-4">
        {groups.map((group) => {
          const bgColour = GROUP_COLOURS[group.colourIndex];
          const textColour = GROUP_TEXT_COLOURS[group.colourIndex];
          const dotColour = GROUP_DOT_COLOURS[group.colourIndex];

          // Calculate allocation bar segments
          // Use securedAmount if available; otherwise split equally
          const facilitiesWithAmounts = group.facilities.map((f) => ({
            ...f,
            amount: f.securedAmount ? parseFloat(f.securedAmount) : null,
          }));

          const totalAmount = facilitiesWithAmounts.reduce(
            (sum, f) => sum + (f.amount ?? 0),
            0
          );
          const hasAmounts = totalAmount > 0;

          const segmentWidths = facilitiesWithAmounts.map((f) => {
            if (!hasAmounts) {
              return (100 / group.facilities.length).toFixed(1);
            }
            return ((f.amount ?? 0) / totalAmount * 100).toFixed(1);
          });

          // Assign a distinct colour per facility within the group
          const SEGMENT_COLOURS = [
            "bg-amber-400",
            "bg-amber-600",
            "bg-blue-400",
            "bg-blue-600",
            "bg-purple-400",
            "bg-purple-600",
          ];

          return (
            <div
              key={group.securityId}
              className={`border-l-4 pl-3 ${bgColour.replace("bg-", "border-")}`}
            >
              {/* Security header */}
              <div className="flex items-center gap-2 mb-1">
                <span
                  className={`inline-block h-2 w-2 rounded-full ${dotColour}`}
                />
                <span className="text-sm font-medium truncate">
                  {group.description}
                </span>
                <span className={`text-xs ${textColour} capitalize`}>
                  {group.securityType.replace(/_/g, " ")}
                </span>
                {group.estimatedValue && (
                  <span className="text-xs text-muted-foreground ml-auto">
                    {formatCurrency(group.estimatedValue)}
                  </span>
                )}
              </div>

              {/* Allocation bar */}
              <div
                className="h-4 rounded overflow-hidden flex mb-1"
                data-testid="allocation-bar"
              >
                {group.facilities.map((facility, idx) => (
                  <div
                    key={facility.facilityId}
                    className={`${SEGMENT_COLOURS[idx % SEGMENT_COLOURS.length]} h-full`}
                    style={{ width: `${segmentWidths[idx]}%` }}
                    title={`${facility.facilityName}: ${
                      facility.securedAmount
                        ? formatCurrency(facility.securedAmount)
                        : "shared"
                    }`}
                  />
                ))}
              </div>

              {/* Facility list */}
              <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                {group.facilities.map((facility, idx) => (
                  <div key={facility.facilityId} className="flex items-center gap-1">
                    <span
                      className={`inline-block h-2 w-2 rounded-full ${SEGMENT_COLOURS[idx % SEGMENT_COLOURS.length]}`}
                    />
                    <span className="text-xs text-muted-foreground">
                      {facility.facilityName}
                      {facility.securedAmount && (
                        <> ({formatCurrency(facility.securedAmount)})</>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

/**
 * Utility: determine the cross-collateral colour index for a given securityId.
 * Used by the securities table to colour-match badges with summary card.
 */
export function getCrossCollateralColourIndex(
  securityId: string,
  crossCollateralSecurityIds: string[]
): number {
  const idx = crossCollateralSecurityIds.indexOf(securityId);
  return idx >= 0 ? idx % GROUP_COLOURS.length : 0;
}

/**
 * Utility: get group colour CSS class for a security's dot indicator.
 */
export function getCrossCollateralDotClass(colourIndex: number): string {
  return GROUP_DOT_COLOURS[colourIndex % GROUP_DOT_COLOURS.length];
}
